/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Grounded in docs/agent-context/subsystems/suppliers-procurement.md
 * Marketing Constraints.
 *
 * CRITICAL facts to honor:
 * - Subcontractors ARE suppliers (they share the suppliers table).
 *   Do NOT market two databases.
 * - 5 unified supplier categories (down from 9 prior). The
 *   supplier_type column was DROPPED. Do NOT market "9 types."
 * - Subcontractors quote per-project — rates are NOT stored on the
 *   record. Do NOT market stored sub rate cards.
 * - 7 supplier detail tabs: overview, materials, catalog, contact,
 *   contract, financial, subcontractors.
 * - 5-section supplier form module.
 * - Performance tracking and analytics wired.
 * - Volume pricing via price-breaks.ts (do NOT reveal the file).
 * - Sync endpoints exist (/api/suppliers/[id]/sync-*) but no named
 *   external integration partner yet — do NOT promise specific
 *   supplier integrations.
 * - PO supplier picker UX needs improvement — do not overclaim polish.
 */
"use client";

import {
  FeaturePageTemplate,
  type FeaturePageConfig,
} from "@/components/landing/feature-page-template";
import {
  Users,
  Star,
  BookOpen,
  FileText,
  DollarSign,
  Handshake,
  TrendingUp,
  ClipboardList,
  ShoppingCart,
  Briefcase,
  Package,
  Calculator,
  Percent,
  LayoutGrid,
} from "lucide-react";

const config: FeaturePageConfig = {
  badge: "Suppliers & Subcontractors",
  title: "Keep your best trade partners",
  titleHighlight: "organized and accountable.",
  description:
    "Track suppliers and subcontractors in one place with contract history, performance notes, quote context, and purchasing visibility so you can choose the right partner on every job.",

  featureGrid: {
    title: "Every trade partner in one system",
    items: [
      {
        icon: Users,
        title: "One Partner Directory",
        description:
          "Keep every trade partner in one directory so your team can find the right contact fast.",
      },
      {
        icon: LayoutGrid,
        title: "Complete Partner Records",
        description:
          "Store contact details, service coverage, contracts, and payment context in one record.",
      },
      {
        icon: Handshake,
        title: "Project-Based Quotes",
        description:
          "Track subcontractor quotes by project so pricing decisions stay tied to real job conditions.",
      },
      {
        icon: Star,
        title: "Performance Tracking",
        description:
          "Track reliability and quality over time so future awards are based on performance, not memory.",
      },
      {
        icon: Percent,
        title: "Volume Pricing",
        description:
          "Support volume price breaks so larger buys get priced the way suppliers actually quote them.",
      },
      {
        icon: BookOpen,
        title: "Service Offerings",
        description:
          "Catalog the trades and services each subcontractor covers. When you're pricing a new job, you know in seconds who can do the work.",
      },
      {
        icon: FileText,
        title: "Contract History",
        description:
          "Track contracts, insurance, expiration dates, and compliance docs per supplier. Never field a 'can I get a certificate of insurance?' call in the middle of a job again.",
      },
      {
        icon: DollarSign,
        title: "Financial Tab",
        description:
          "Payment terms, historical spend, outstanding balances, and billing history per supplier. Your trade partner relationship at a glance.",
      },
      {
        icon: ShoppingCart,
        title: "Wired Into Purchase Orders",
        description:
          "Every PO attaches to a supplier and receiving attributes spend to the right partner. Estimate linkage can prefill supplier context to speed purchasing workflows.",
      },
    ],
  },

  workflow: {
    title: "What working with trade partners looks like in CC",
    description:
      "A practical process for onboarding, selecting, and managing suppliers and subcontractors across every job.",
    steps: [
      {
        label: "Onboard",
        title: "Set up a partner record once",
        description:
          "Capture company, contact, service, and compliance context so your team can work confidently with the right partner from day one.",
        details: [
          "Standardized onboarding data",
          "Faster partner lookup by estimators and PMs",
          "Cleaner records for purchasing and billing",
          "Better continuity across teams",
        ],
      },
      {
        label: "Organize",
        title: "Classify each partner by what they do best",
        description:
          "Tag service coverage and categories so your estimators shortlist qualified partners quickly when pricing work.",
        details: [
          "Service-based filtering support",
          "Category-driven partner discovery",
          "Cleaner bid-day partner selection",
          "Less guesswork for estimating teams",
        ],
      },
      {
        label: "Quote",
        title: "Track quotes in job context",
        description:
          "Keep subcontractor pricing tied to the specific project so quote comparisons reflect real scope and conditions.",
        details: [
          "Project-specific quote capture",
          "Estimate-linked quote history",
          "Side-by-side comparison support",
          "Stronger award decisions",
        ],
      },
      {
        label: "Buy",
        title: "Issue purchase orders with better context",
        description:
          "Move from estimate to purchasing with supplier context already in place so ordering is faster and less error-prone.",
        details: [
          "Estimate-to-PO continuity",
          "Supplier-aware ordering flow",
          "Order lifecycle tracking support",
          "Improved receiving accountability",
        ],
      },
      {
        label: "Settle",
        title: "Track spend and payment history",
        description:
          "Capture bills and payment context so supplier relationships and project costs stay transparent.",
        details: [
          "PO-linked billing context",
          "Spend visibility by partner",
          "Payment history awareness",
          "Better vendor relationship management",
        ],
      },
      {
        label: "Evaluate",
        title: "Use performance history to choose better partners",
        description:
          "Use delivery and reliability history to guide future awards and reduce avoidable margin loss.",
        details: [
          "Partner performance signal tracking",
          "Long-term reliability visibility",
          "Higher-confidence award decisions",
          "Continuous trade partner optimization",
        ],
      },
    ],
  },

  capabilities: [
    {
      icon: Users,
      title: "One Directory for Trade Partners",
      description:
        "Treat suppliers and subcontractors as one partner ecosystem so your history, contacts, and decision context stay coherent.",
      bullets: [
        "Unified partner record model",
        "Shared history across estimating and purchasing",
        "Less duplicate data entry",
        "Cleaner collaboration between teams",
        "Better continuity from bid to close-out",
      ],
    },
    {
      icon: Handshake,
      title: "Project-Specific Subcontractor Quotes",
      description:
        "Capture quotes in the scope where they belong so comparisons are grounded in real project requirements.",
      bullets: [
        "Estimate-level quote capture",
        "Historical quote context by partner",
        "Better apples-to-apples comparisons",
        "Cleaner award justification",
        "Reduced pricing confusion",
      ],
    },
    {
      icon: Star,
      title: "Performance-Based Partner Decisions",
      description:
        "Track reliability and quality over time so your strongest partners earn more work and weak links get flagged early.",
      bullets: [
        "Reliability trend visibility",
        "Quality and delivery context",
        "Stronger partner scorecards",
        "Better risk management on critical scopes",
        "Improved long-term partner mix",
      ],
    },
    {
      icon: Percent,
      title: "Volume-Aware Material Pricing",
      description:
        "Apply realistic quantity-based pricing so larger scopes are not estimated with small-order assumptions.",
      bullets: [
        "Quantity-break pricing support",
        "More realistic large-scope estimates",
        "Less manual spreadsheet work",
        "Improved procurement planning",
        "Stronger gross-margin protection",
      ],
    },
  ],

  connections: {
    title: "How suppliers and subcontractors connect to the rest of CC",
    items: [
      {
        icon: ShoppingCart,
        flow: "Supplier → Purchase Orders",
        description:
          "Every PO attaches to a supplier. Generate POs from estimates and the right supplier pre-fills based on your cost database linkage.",
      },
      {
        icon: Calculator,
        flow: "Cost Database → Supplier Linkage",
        description:
          "Materials in your cost database can reference supplier pricing and volume breaks to improve estimate and purchasing decisions.",
      },
      {
        icon: Briefcase,
        flow: "Subcontractor → Estimate Quote",
        description:
          "Record subcontractor quotes on the estimates they're pricing. Side-by-side comparison when multiple subs bid the same scope.",
      },
      {
        icon: TrendingUp,
        flow: "Performance → Supplier Optimization",
        description:
          "Aggregated performance scores feed supplier optimization recommendations — who's paying off, who's slowing you down, who's worth keeping around.",
      },
    ],
  },

  relatedFeatures: [
    {
      label: "Purchase Orders",
      href: "/features/purchase-orders",
      icon: ShoppingCart,
    },
    {
      label: "Cost Database",
      href: "/features/cost-database",
      icon: Package,
    },
    {
      label: "Estimates & Takeoffs",
      href: "/features/estimates",
      icon: Calculator,
    },
    {
      label: "Project Management",
      href: "/features/project-management",
      icon: Briefcase,
    },
    {
      label: "Budgets & Invoices",
      href: "/features/budgets-invoices",
      icon: DollarSign,
    },
    {
      label: "Documents",
      href: "/features/documents",
      icon: ClipboardList,
    },
  ],
};

export function SubcontractorsPageClient() {
  return <FeaturePageTemplate config={config} />;
}
